import React, {useEffect, useMemo, useState} from "react";
import createTheme from "@mui/material/styles/createTheme";
import CssBaseline from "@mui/material/CssBaseline";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import {get, post} from "./requests";
import FilterDialog from "./Components/FilterDialog";
import Loader from "./Components/Loader";
import Message from "./Components/Message";
import ResultTable from "./Components/ResultTable";
import {RecommendationsTable} from "./Components/RecommendationsTable";
import {
    AUTHORIZED_STATE_DONE, AUTHORIZED_STATE_NO_ORG_ID,
    AUTHORIZED_STATE_NONE,
    RESULT_GROUP_WORKER,
    RESULT_GROUP_NONE,
    DATE_FORMAT_DATE,
    TIME_FORMAT_HOURS,
    DATE_FORMAT,
} from "./constants";
import Link from "@mui/material/Link";
import InitialConfigDialog from "./Components/InitialConfigDialog";
import './App.css';
import {makeObjectFromArray, pushAnalytics, sliceIntoChunks, sleep} from "./helpers";
import ChangelogDialog from "./Components/ChangelogDialog";
import InstructionCard from "./Components/InstructionCard";
import {Trans, useTranslation} from 'react-i18next';
import {
    usersMapAtom,
    queuesMapAtom,
    groupsMapAtom,
    projectsMapAtom,
    boardsMapAtom,
    issueTypesMapAtom,
    issueStatusesMapAtom,
    haveWorkLogsAtom, myUserAtom, colorThemeAtom, localeAtom, insightsEnabledAtom,
} from "./jotai/atoms";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {AuthorizeButtonsContainer} from "./Components/AuthorizeButtonsContainer";
import {OrganizationSelectorContainer} from "./Components/OrganizationSelectorContainer";
import {YandexTokenDialog} from "./Components/YandexTokenDialog";
import {useLoader, useMessage, useFilterResult} from "./hooks";
import {SettingsButton} from "./Components/SettingsButton";
import {Button} from "@mui/material";
import {initKeycloak, getKeycloakService} from "./services/keycloak";
import moment from "moment";

function App() {
    const {t} = useTranslation();

    const {startLoading, endLoading, setLoadingValue} = useLoader();
    const {showSuccess, showError} = useMessage();
    const {setFilteredData} = useFilterResult();

    const setUsersMap = useSetAtom(usersMapAtom);
    const setQueuesMap = useSetAtom(queuesMapAtom);
    const setGroupsMap = useSetAtom(groupsMapAtom);
    const setProjectsMap = useSetAtom(projectsMapAtom);
    const setBoardsMap = useSetAtom(boardsMapAtom);
    const setIssueTypesMap = useSetAtom(issueTypesMapAtom);
    const setIssueStatusesMap = useSetAtom(issueStatusesMapAtom);
    const haveDataToDisplay = useAtomValue(haveWorkLogsAtom);
    const colorTheme  = useAtomValue(colorThemeAtom);
    const locale = useAtomValue(localeAtom);
    const insightsEnabled = useAtomValue(insightsEnabledAtom);

    const [myUser, setMyUser] = useAtom(myUserAtom);


    const bypassAuthorization = ["localhost", "127.0.0.1"].includes(window.location.hostname);

    const [filtered, setFiltered] = useState(false);
    const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf('isoWeek'));
    const [timesheetFilterApplied, setTimesheetFilterApplied] = useState(false);

    const [authorized, setAuthorized] = useState(bypassAuthorization ? AUTHORIZED_STATE_DONE : AUTHORIZED_STATE_NONE);

    const [filterDialogState, setFilterDialogState] = useState(false);

    const [initialConfigDialogState, setInitialConfigDialogState] = useState(false);

    const [allowManualInput, setAllowManualInput] = useState(false);
    const [defaultOrgId, setDefaultOrgId] = useState("");
    const [federationId, setFederationId] = useState("");
    const [yandexTokenDialogOpen, setYandexTokenDialogOpen] = useState(false);
    const [checkingToken, setCheckingToken] = useState(false);
    const [hasFilterAccess, setHasFilterAccess] = useState(false);

    const boot = () => {
        startLoading();

        // Initialize Keycloak if configured
        const keycloakConfig = {
            realmUrl: process.env.REACT_APP_KEYCLOAK_REALM_URL,
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
        };

        if (keycloakConfig.realmUrl && keycloakConfig.clientId) {
            console.log('Keycloak configured, initializing...', keycloakConfig);
            const keycloakService = initKeycloak(keycloakConfig);
            if (keycloakService) {
                // Set timeout to show dialog if Keycloak takes too long
                const timeoutId = setTimeout(() => {
                    console.log('Keycloak initialization timeout, showing token dialog');
                    endLoading();
                    setYandexTokenDialogOpen(true);
                }, 5000); // 5 seconds timeout
                
                keycloakService.init().then(() => {
                    clearTimeout(timeoutId);
                    console.log('Keycloak initialized, authenticated:', keycloakService.isAuthenticated());
                    // If Keycloak is configured, it will automatically redirect to login
                    // if user is not authenticated (onLoad: 'login-required')
                    // If user is authenticated, check for Yandex token first
                    if (keycloakService.isAuthenticated()) {
                        // Check if user has Yandex token linked before continuing
                        console.log('Keycloak authenticated, checking Yandex token status...');
                        setCheckingToken(true);
                        get("/api/v1/user_token/status")
                            .then(tokenStatus => {
                                setCheckingToken(false);
                                console.log('Token status response (after Keycloak auth):', tokenStatus);
                                if (!tokenStatus || !tokenStatus.has_token) {
                                    // User needs to link Yandex token
                                    console.log('No Yandex token found, showing dialog');
                                    endLoading();
                                    setYandexTokenDialogOpen(true);
                                    return;
                                }
                                
                                // User has token, load it from database and save to localStorage
                                console.log('Yandex token found, loading from database...');
                                get("/api/v1/user_token")
                                    .then(tokenResponse => {
                                        if (tokenResponse && tokenResponse.yandex_token) {
                                            // Save token to localStorage
                                            localStorage.setItem("authToken", tokenResponse.yandex_token);
                                            console.log('Yandex token loaded and saved to localStorage');
                                        }
                                        // Continue with boot
                                        continueBoot();
                                    })
                                    .catch(err => {
                                        console.error('Failed to load token from database:', err);
                                        // Continue anyway - token might still work from previous session
                                        continueBoot();
                                    });
                            })
                            .catch(err => {
                                setCheckingToken(false);
                                console.error('Failed to check token status after Keycloak auth:', err);
                                console.log('Error details:', err.code, err.message);
                                // Show dialog on error to allow user to set token
                                console.log('Showing token dialog due to error');
                                endLoading();
                                setYandexTokenDialogOpen(true);
                            });
                    } else {
                        // If not authenticated and not redirected (due to CORS or other issues),
                        // show token dialog as fallback
                        console.log('Keycloak not authenticated and no redirect happened, showing token dialog');
                        endLoading();
                        // Check token status first before showing dialog
                        get("/api/v1/user_token/status")
                            .then(tokenStatus => {
                                console.log('Token status (no Keycloak):', tokenStatus);
                                if (!tokenStatus || !tokenStatus.has_token) {
                                    setTimeout(() => {
                                        setYandexTokenDialogOpen(true);
                                    }, 100);
                                } else {
                                    // Token exists, continue with boot
                                    continueBoot();
                                }
                            })
                            .catch(err => {
                                console.error('Failed to check token status:', err);
                                // Show dialog on error
                                setTimeout(() => {
                                    setYandexTokenDialogOpen(true);
                                }, 100);
                            });
                    }
                }).catch((error) => {
                    clearTimeout(timeoutId);
                    console.error('Keycloak initialization error:', error);
                    // Keycloak init failed - show token dialog as fallback
                    console.log('Keycloak failed, showing token dialog as fallback');
                    endLoading();
                    // Use setTimeout to ensure state update happens after render
                    setTimeout(() => {
                        setYandexTokenDialogOpen(true);
                    }, 100);
                });
            } else {
                console.log('Keycloak service not created, showing token dialog');
                endLoading();
                setTimeout(() => {
                    setYandexTokenDialogOpen(true);
                }, 100);
            }
        } else {
            console.log('Keycloak not configured, using legacy auth');
            continueBoot();
        }
    };

    const continueBoot = () => {
        // Get Keycloak config for later use
        const keycloakConfig = {
            realmUrl: process.env.REACT_APP_KEYCLOAK_REALM_URL,
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
        };
        
        get("/api/v1/config")
            .then(data => {
                if (!data.haveConfig) {
                    setInitialConfigDialogState(true);
                } else {
                    if (data.organizationId !== '') {
                        localStorage.setItem("orgId", data.organizationId); // У нас МОЖЕТ быть установлена организация
                        setDefaultOrgId(data.organizationId);
                    }

                    setAllowManualInput(data.allowManualInput);
                    setFederationId(data.federationId);

                    const token = localStorage.getItem("authToken");
                    const orgId = localStorage.getItem("orgId");
                    const iAmToken = localStorage.getItem("iAmToken");
                    
                    // Check Keycloak token
                    const keycloakService = getKeycloakService();
                    const keycloakToken = keycloakService?.getToken();

                    if (bypassAuthorization) {
                        // При bypassAuthorization не открываем диалог фильтра,
                        // так как автоматически применяется фильтр Timesheet
                        return setAuthorized(AUTHORIZED_STATE_DONE);
                    }

                    // If Keycloak is configured, it should handle authentication
                    // If we reach here and Keycloak is configured but user is not authenticated,
                    // something went wrong with the redirect
                    if (keycloakConfig.realmUrl && keycloakConfig.clientId) {
                        // Check if Keycloak service is available and authenticated
                        if (!keycloakService) {
                            console.warn('Keycloak service not available, showing token dialog');
                            setYandexTokenDialogOpen(true);
                            return;
                        }
                        
                        if (keycloakToken && keycloakService?.isAuthenticated()) {
                            // User is authenticated via Keycloak
                            // Check if user has Yandex token linked
                            setCheckingToken(true);
                            console.log('Checking Yandex token status for Keycloak user...');
                            get("/api/v1/user_token/status")
                                .then(tokenStatus => {
                                    setCheckingToken(false);
                                    console.log('Token status response:', tokenStatus);
                                    if (!tokenStatus || !tokenStatus.has_token) {
                                        // User needs to link Yandex token
                                        console.log('No Yandex token found, showing dialog');
                                        // Force show dialog even if already open
                                        setYandexTokenDialogOpen(true);
                                        return;
                                    }
                                    
                                    // User has token, load it from database and save to localStorage
                                    console.log('Yandex token found, loading from database...');
                                    get("/api/v1/user_token")
                                        .then(tokenResponse => {
                                            if (tokenResponse && tokenResponse.yandex_token) {
                                                // Save token to localStorage
                                                localStorage.setItem("authToken", tokenResponse.yandex_token);
                                                console.log('Yandex token loaded and saved to localStorage');
                                            }
                                            // Close dialog if it was open
                                            if (yandexTokenDialogOpen) {
                                                setYandexTokenDialogOpen(false);
                                            }
                                            // Токен теперь точно загружен, можно устанавливать authorized
                                            if (orgId && !data.allowManualInput) {
                                                return setAuthorized(AUTHORIZED_STATE_DONE);
                                            } else if (orgId) {
                                                return setAuthorized(AUTHORIZED_STATE_NO_ORG_ID);
                                            }
                                            // If no orgId, show org selector
                                        })
                                        .catch(err => {
                                            console.error('Failed to load token from database:', err);
                                            // Continue anyway - token might still work from previous session
                                            if (yandexTokenDialogOpen) {
                                                setYandexTokenDialogOpen(false);
                                            }
                                            if (orgId && !data.allowManualInput) {
                                                return setAuthorized(AUTHORIZED_STATE_DONE);
                                            } else if (orgId) {
                                                return setAuthorized(AUTHORIZED_STATE_NO_ORG_ID);
                                            }
                                        });
                                    return; // Don't continue with legacy auth
                                })
                                .catch(err => {
                                    setCheckingToken(false);
                                    console.error('Failed to check token status:', err);
                                    console.log('Error details:', err.code, err.message);
                                    // If error is 401/403, user might not be authenticated properly
                                    // If error is 500, database might not be available
                                    // In any case, show dialog to allow user to set token
                                    console.log('Showing token dialog due to error');
                                    setYandexTokenDialogOpen(true);
                                    return;
                                });
                            return; // Don't continue with legacy auth
                        } else {
                            // Keycloak is configured but user is not authenticated
                            // This shouldn't happen if onLoad: 'login-required' worked
                            // But if it does, show token dialog as fallback
                            console.warn('Keycloak is configured but user is not authenticated, showing token dialog');
                            setYandexTokenDialogOpen(true);
                        }
                    }

                    if ((token || iAmToken) && orgId && !allowManualInput) {
                        return setAuthorized(AUTHORIZED_STATE_DONE);
                    }

                    if (token || iAmToken) {
                        return setAuthorized(AUTHORIZED_STATE_NO_ORG_ID);
                    }

                    if (window.location.hash) {
                        const urlSearchParams = new URLSearchParams(window.location.hash.replace("#", "?"));
                        const params = Object.fromEntries(urlSearchParams.entries());

                        if (params.error_description) {
                            showError(params.error_description);
                        } else {
                            window.history.replaceState({}, document.title, "/");
                            localStorage.setItem("authToken", params.access_token);

                            if (orgId && !data.allowManualInput) {
                                setAuthorized(AUTHORIZED_STATE_DONE);
                            } else {
                                setAuthorized(AUTHORIZED_STATE_NO_ORG_ID);
                            }

                            showSuccess(t('notifications:success_authorization'));
                        }
                    }
                }
            })
            .catch(showError)
            .finally(() => {
                if (!checkingToken) {
                    endLoading();
                }
            });
    };

    const handleInitialConfigComplete = () => {
        setInitialConfigDialogState(false);
        boot();
    };

    useEffect(() => {
        boot();

        pushAnalytics('applicationBooted', {
            locale,
            colorTheme,
            insightsEnabled
        });
    }, []);

    useEffect(() => {
        if (authorized === AUTHORIZED_STATE_DONE) {
            startLoading();

            const fetchData = async () => {
                const [me, usersAndGroups, queues, boards, projects, issueTypes, issueStatuses] = await Promise.all([
                    get("/api/v1/me"),
                    get("/api/v1/users_and_groups"),
                    get("/api/v1/queues"),
                    get("/api/v1/boards"),
                    get("/api/v1/projects"),
                    get("/api/v1/issue_types"),
                    get("/api/v1/issue_statuses")
                ]);

                return {
                    me: me.data,
                    users: usersAndGroups.users,
                    queues: queues.data,
                    groups: usersAndGroups.groups,
                    boards: boards.data,
                    projects: projects.data,
                    issueTypes: issueTypes.data,
                    issueStatuses: issueStatuses.issueStatuses
                };
            };

            // Check if Keycloak is configured and wait for it to be ready
            const keycloakConfig = {
                realmUrl: process.env.REACT_APP_KEYCLOAK_REALM_URL,
                clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
            };
            
            const keycloakService = getKeycloakService();
            const waitForKeycloak = () => {
                return new Promise((resolve) => {
                    if (!keycloakConfig.realmUrl || !keycloakConfig.clientId) {
                        // Keycloak not configured, proceed immediately
                        resolve();
                        return;
                    }
                    
                    if (keycloakService && keycloakService.isAuthenticated()) {
                        // Keycloak is ready
                        resolve();
                        return;
                    }
                    
                    // Wait for Keycloak to initialize (max 5 seconds)
                    let attempts = 0;
                    const checkInterval = setInterval(() => {
                        attempts++;
                        if (keycloakService && keycloakService.isAuthenticated()) {
                            clearInterval(checkInterval);
                            resolve();
                        } else if (attempts >= 50) {
                            // Timeout after 5 seconds (50 * 100ms)
                            clearInterval(checkInterval);
                            console.warn('Keycloak initialization timeout, proceeding anyway');
                            resolve();
                        }
                    }, 100);
                });
            };

            waitForKeycloak().then(() => {
                get("/api/v1/ping")
                .then(() => {
                    fetchData()
                        .then(({me, users, queues, groups, boards, projects, issueTypes, issueStatuses}) => {
                            setMyUser({
                                value: me.id,
                                label: me.display,
                                isAdmin: me.isAdministrator,
                                isReadOnly: !me.hasLicense
                            });

                            setBoardsMap(makeObjectFromArray(boards, board => board.id, board => ({
                                value: board.id,
                                label: `[${board.id}]: ${board.attributes.name}`
                            })));

                            setIssueStatusesMap(makeObjectFromArray(issueStatuses, status => status.key, status => ({
                                value: status.key,
                                label: `[${status.key}] ${status.name}`
                            })))

                            setIssueTypesMap(makeObjectFromArray(issueTypes, type => type.id, type => ({
                                value: type.id,
                                label: `[${type.key}] ${type.name}`
                            })));

                            setUsersMap(makeObjectFromArray(users, user => user.id, user => ({
                                value: user.id,
                                label: user.display ? `${user.display}, ${user.email}` : user.email,
                                email: user.email
                            })));

                            setProjectsMap(makeObjectFromArray(projects, project => project.id, project => ({
                                value: project.id,
                                label: project.name
                            })));

                            setQueuesMap(makeObjectFromArray(queues, queue => queue.id, queue => ({
                                value: queue.id,
                                label: `${queue.id}: ${queue.attributes.name}`,
                                title: queue.attributes.name
                            })));

                            setGroupsMap(makeObjectFromArray(groups, group => group.id, group => ({
                                value: group.id,
                                label: group.attributes.label,
                                members: group.attributes.members
                            })));

                            // Check filter access
                            get("/api/v1/filter_access")
                                .then(response => {
                                    console.log('Filter access response:', response);
                                    console.log('User login from API:', response.user_login);
                                    console.log('Has access:', response.has_access);
                                    setHasFilterAccess(response.has_access || false);
                                })
                                .catch(err => {
                                    console.error('Failed to check filter access:', err);
                                    setHasFilterAccess(false);
                                });

                            // Автоматически применяем фильтр Timesheet при загрузке
                            const userData = {
                                value: me.id,
                                label: me.display,
                                isAdmin: me.isAdministrator,
                                isReadOnly: !me.hasLicense
                            };
                            
                            if (userData.value && !timesheetFilterApplied) {
                                // Применяем фильтр Timesheet после загрузки всех данных
                                setTimesheetFilterApplied(true);
                                
                                // Проверяем наличие токена и применяем фильтр
                                const applyFilterWithTokenCheck = (attempts = 0) => {
                                    const token = localStorage.getItem("authToken");
                                    const keycloakService = getKeycloakService();
                                    const keycloakToken = keycloakService?.getToken();
                                    
                                    if (token || keycloakToken) {
                                        // Токен есть, применяем фильтр
                                        console.log('Token found, applying timesheet filter');
                                        applyTimesheetFilter(userData.value, moment().startOf('isoWeek'));
                                    } else if (attempts < 20) {
                                        // Токена еще нет, ждем и проверяем снова (максимум 10 секунд)
                                        console.log(`Token not found yet, attempt ${attempts + 1}/20, retrying...`);
                                        setTimeout(() => {
                                            applyFilterWithTokenCheck(attempts + 1);
                                        }, 500);
                                    } else {
                                        console.error('Token not found after 20 attempts, cannot apply timesheet filter');
                                        // Не применяем фильтр, если токен так и не появился
                                    }
                                };
                                
                                // Начинаем проверку с задержкой, чтобы дать время токену загрузиться
                                setTimeout(() => {
                                    applyFilterWithTokenCheck();
                                }, 500);
                            } else if (!userData.value) {
                                setFilterDialogState(true);
                            }
                        }).catch((e) => {
                        showError(e);
                        if (!bypassAuthorization) {
                            exit();
                        }
                    });
                }).catch(() => {
                    if (!bypassAuthorization) {
                        exit();
                    }
                }).finally(() => {
                    endLoading();
                });
            });
        }
    }, [authorized]);

    const onFilterApply = () => {
        setFiltered(true);
        setFilterDialogState(false); // Закрываем диалоговое окно
    };

    // Генератор моментов для дат
    function* generateMoments(start, end, step) {
        const variableMoment = start.clone();

        while (true) {
            yield variableMoment.clone();

            variableMoment.add(step);
            if (variableMoment >= end) {
                if (!variableMoment.isAfter(end)) {
                    yield variableMoment.clone();
                }
                break;
            }
        }
    }

    // Подготовка дат для отображения
    const prepareDates = (dates, dateFormat) => {
        if (dateFormat === DATE_FORMAT_DATE) {
            return dates.map(date => ({
                index: date.format(DATE_FORMAT),
                date,
                grouped: false,
                includes: [date]
            }));
        }
        return dates.map(date => ({
            index: date.format(DATE_FORMAT),
            date,
            grouped: false,
            includes: [date]
        }));
    };

    // Применение фильтра Timesheet
    const applyTimesheetFilter = async (userId = null, weekStart = null) => {
        const targetUserId = userId || (myUser && myUser.value);
        if (!targetUserId) {
            showError(t('notifications:user_not_selected'));
            return;
        }

        // Проверяем наличие токена перед применением фильтра
        const token = localStorage.getItem("authToken");
        const keycloakService = getKeycloakService();
        const keycloakToken = keycloakService?.getToken();
        
        console.log('applyTimesheetFilter: token check', {
            hasYandexToken: !!token,
            hasKeycloakToken: !!keycloakToken,
            userId: targetUserId
        });
        
        if (!token && !keycloakToken) {
            console.warn('No token available, cannot apply timesheet filter');
            // Не применяем фильтр, если токена нет
            return;
        }

        // Закрываем диалог фильтра сразу, чтобы он не показывался поверх timesheet
        setFilterDialogState(false);

        // Используем переданную неделю или текущую
        const weekStartMoment = weekStart ? moment(weekStart) : currentWeekStart;
        const monday = weekStartMoment.clone().startOf('isoWeek'); // Понедельник выбранной недели
        const sunday = weekStartMoment.clone().endOf('isoWeek'); // Воскресенье выбранной недели
        
        // Обновляем состояние текущей недели
        setCurrentWeekStart(monday.clone());

        startLoading();

        const fetchData = async filter => {
            const response = await post("/api/v1/result", filter);
            return {
                workLogs: response.workLogs,
            };
        };

        const dates = [...generateMoments(monday, sunday, moment.duration({days: 1}))];
        const realDates = prepareDates(dates, DATE_FORMAT_DATE);

        const fetchChunks = async chunks => {
            const out = [];
            const total = chunks.length;
            let loaded = 0;

            if (chunks.length !== 1) {
                setLoadingValue(0);
            }

            const filter = {
                userIdentities: [String(targetUserId)],
                queues: [],
                projects: [],
                issueTypes: []
            };

            const loadChunks = async chunks => {
                const failedChunks = [];

                for (const chunk of chunks) {
                    try {
                        filter.dateFrom = chunk[0].format();
                        filter.dateTo = chunk[chunk.length - 1].format();

                        const {workLogs} = await fetchData(filter);
                        loaded++;

                        if (chunks.length !== 1) {
                            setLoadingValue(parseInt(100 / (total / loaded)));
                        }

                        out.push(...workLogs);
                        await sleep(250);
                    } catch (e) {
                        failedChunks.push(chunk);
                    }
                }

                return {failedChunks};
            };

            const {failedChunks} = await loadChunks(chunks);

            if (failedChunks.length > 0) {
                await loadChunks(failedChunks);
            }

            return {
                dateFrom: monday,
                dateTo: sunday,
                workLogs: out
            };
        };

        const chunks = sliceIntoChunks(dates, 20);

        try {
            const {workLogs} = await fetchChunks(chunks);

            setFilteredData({
                timeFormat: TIME_FORMAT_HOURS,
                dateFormat: DATE_FORMAT_DATE,
                resultGroups: [RESULT_GROUP_WORKER, RESULT_GROUP_NONE],
                workLogs: workLogs,
                selectedUsers: [String(targetUserId)],
                dates: realDates,
                hideDetails: false,
                highlightTime: false,
            });

            setFiltered(true);
            setFilterDialogState(false); // Закрываем диалог фильтра, если он был открыт

            pushAnalytics('timesheetFilterApplied', {
                dateFormat: DATE_FORMAT_DATE,
                usersCount: 1,
                daysCount: dates.length,
                resultGroup: [RESULT_GROUP_WORKER, RESULT_GROUP_NONE].join("-"),
                timeFormat: TIME_FORMAT_HOURS
            });
        } catch (error) {
            showError(error);
        } finally {
            endLoading();
        }
    };

    const exit = async () => {
        // Check if Keycloak is configured and user is authenticated
        const keycloakService = getKeycloakService();
        const keycloakConfig = {
            realmUrl: process.env.REACT_APP_KEYCLOAK_REALM_URL,
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
        };

        if (keycloakConfig.realmUrl && keycloakConfig.clientId && keycloakService && keycloakService.isAuthenticated()) {
            // Logout from Keycloak - this will redirect to Keycloak logout page
            // After logout, Keycloak will redirect back to the app, which will trigger login flow
            try {
                // Clear local storage first
                localStorage.removeItem("authToken");
                localStorage.removeItem("iAmToken");
                if (allowManualInput) {
                    localStorage.removeItem("orgId");
                }
                
                // Logout from Keycloak - redirects to login page
                await keycloakService.logout({
                    redirectUri: window.location.origin + window.location.pathname
                });
                // Note: The redirect will happen automatically, so code below won't execute
            } catch (error) {
                console.error('Keycloak logout error:', error);
                // If logout fails, still clear local data and reset state
                localStorage.removeItem("authToken");
                localStorage.removeItem("iAmToken");
                if (allowManualInput) {
                    localStorage.removeItem("orgId");
                }
                setAuthorized(bypassAuthorization ? AUTHORIZED_STATE_DONE : AUTHORIZED_STATE_NONE);
                showSuccess(t('notifications:logout'));
            }
        } else {
            // Legacy logout - clear local storage and reset state
            localStorage.removeItem("authToken");
            localStorage.removeItem("iAmToken");

            if( allowManualInput ) {
                localStorage.removeItem("orgId");
            }

            setAuthorized(bypassAuthorization ? AUTHORIZED_STATE_DONE : AUTHORIZED_STATE_NONE);

            showSuccess(t('notifications:logout'));
        }
    };

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: colorTheme,
                    weekend: {
                        main: '#F9F7F2',
                        light: '#F9F7F2',
                        dark: '#757575',
                        contrastText: '#242105',
                    },
                    yandex: {
                        main: '#000000',
                        light: '#F9F7F2',
                        dark: '#9E9E9E',
                        contrastText: '#FFFFFF',

                        contrastThreshold: 4.5,
                        tonalOffset: 0.3,
                    }
                },
            }),
        [colorTheme],
    );

    return <ThemeProvider theme={theme}>
        <CssBaseline/>

        <Loader/>
        <Message/>

        <InitialConfigDialog state={initialConfigDialogState} handleClose={() => setInitialConfigDialogState(false)} handleComplete={handleInitialConfigComplete}/>

        <FilterDialog
            handleClose={() => setFilterDialogState(false)}
            state={filterDialogState}
            showError={showError}
            onApply={onFilterApply}
        />

        <ChangelogDialog />

        <Box
            sx={{
                display: 'flex',
                position: 'relative',
                flexDirection: 'column',
                minHeight: '100vh',
            }}
        >
            {authorized === AUTHORIZED_STATE_DONE && <AppBar
                position="absolute"
                color="default"
                elevation={0}
                sx={{
                    position: 'relative',
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Toolbar sx={{flexWrap: 'wrap'}}>
                    <Typography variant="h6" color="inherit" noWrap
                                sx={{flexGrow: 1, display: {xs: 'none', md: 'block'}}}>
                        {t('components:app.welcome', {userLabel: myUser.label})}
                    </Typography>
                    <nav>
                        <Button color="inherit" onClick={() => {
                            const prevWeek = currentWeekStart.clone().subtract(1, 'week');
                            applyTimesheetFilter(null, prevWeek);
                            pushAnalytics('previousWeekClick');
                        }}>
                            {t('common:button.previous_week')}
                        </Button>
                        <Button color="inherit" onClick={() => {
                            const nextWeek = currentWeekStart.clone().add(1, 'week');
                            applyTimesheetFilter(null, nextWeek);
                            pushAnalytics('nextWeekClick');
                        }}>
                            {t('common:button.next_week')}
                        </Button>
                        <Button color="inherit" onClick={() => {
                            applyTimesheetFilter();
                            pushAnalytics('timesheetButtonClick');
                        }}>
                            {t('common:button.timesheet')}
                        </Button>
                        {hasFilterAccess && <Button color="inherit" onClick={() => {
                            setFilterDialogState(true);
                            pushAnalytics('filterButtonClick');
                        }}>
                            {t('common:button.filter')}
                        </Button>}
                        <Button color="inherit" onClick={() => {
                            exit();
                            pushAnalytics('exitButtonClick')
                        }}>
                            {t('common:button.exit')}
                        </Button>
                    </nav>
                </Toolbar>
            </AppBar>}
            <Container component="main" sx={{mt: 2, mb: 2}} maxWidth={false}>
                {authorized === AUTHORIZED_STATE_DONE && <Grid container spacing={2}>
                    <Grid size={12}>
                        {!filtered && !haveDataToDisplay && <div><Trans
                            i18nKey='components:app.not_enough_data'
                            components={{
                                filterLink: <Link href="#" onClick={() => setFilterDialogState(true)}/>
                            }}
                        /></div>}
                        {(filtered || haveDataToDisplay) && <ResultTable/>}
                    </Grid>
                    <Grid size={12}>
                        <RecommendationsTable/>
                    </Grid>
                    <Grid size={{xs: 12, sm: 10, md: 8, lg: 6, xl: 6}} offset={{xs: 0, sm: 1, md: 2, lg: 3, xl: 3}}>
                        <InstructionCard/>
                    </Grid>
                </Grid>}
                {!bypassAuthorization && authorized === AUTHORIZED_STATE_NONE &&
                    <AuthorizeButtonsContainer allowManualInput={allowManualInput}
                                               federationId={federationId} defaultOrgId={defaultOrgId}
                                               setAuthorized={setAuthorized}/>}
                {authorized === AUTHORIZED_STATE_NO_ORG_ID &&
                    <OrganizationSelectorContainer defaultOrgId={defaultOrgId} setAuthorized={setAuthorized}
                                                   setFilterDialogState={setFilterDialogState} exit={exit}/>}
            </Container>

            <SettingsButton />
            
            <YandexTokenDialog
                open={yandexTokenDialogOpen}
                onClose={() => setYandexTokenDialogOpen(false)}
                onTokenSet={() => {
                    setYandexTokenDialogOpen(false);
                    // Reload to continue with authorization
                    window.location.reload();
                }}
            />
        </Box>
    </ThemeProvider>
}

export default App;
